"use client";

import {type HTMLInputAutoCompleteAttribute, JSX, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";

import {useDetectClickOutside} from "@/shared/hooks/dom/use-detect-click-outside";
import {useScrollIntoView} from "@/shared/hooks/dom/use-scroll-into-view";
import {useModelSnapshot} from "@/shared/hooks/state-management/use-model-snapshot";
import {useTranslation} from "@/shared/i18n";
import {type PhoneFieldInstance} from "@/shared/models/form/phone";
import {FloatingDropdown} from "@/shared/ui/overlay/floating-dropdown";
import {handlePasteAtCursor} from "@/shared/utils";

import {CountryDropdown} from "./parts";

import "./styles/phone-field.scss";

interface PhoneFieldProps {
    model: PhoneFieldInstance;
    label?: string;
    labelVariant?: "default" | "subtle";
    fontSize?: "default" | "small" | "flexible";
    height?: "default" | "small" | "flexible";
    className?: string;
    showFlag?: boolean;
    autoComplete?: HTMLInputAutoCompleteAttribute;
}

export function PhoneFieldComponent({
    model,
    label,
    labelVariant = "default",
    fontSize = "default",
    height = "default",
    className,
    showFlag = false,
    autoComplete = "tel",
}: PhoneFieldProps): JSX.Element {
    const {t} = useTranslation("forms");
    const {t: tValidation} = useTranslation("validation");
    const snapshot = useModelSnapshot(model);
    const [prefixWidth, setPrefixWidth] = useState(27); // Default width for "+1" prefix (~27px + 10px padding = 37px)
    const [hasInteracted, setHasInteracted] = useState(false);
    const prefixRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const labelClass = labelVariant === "subtle" ? "phone-field__label--subtle" : "";
    const fontSizeClass =
        fontSize === "small" ? "phone-field__input--font-small" : fontSize === "flexible" ? "phone-field__input--font-flexible" : "";
    const prefixFontSizeClass =
        fontSize === "small" ? "phone-field__prefix--font-small" : fontSize === "flexible" ? "phone-field__prefix--font-flexible" : "";
    const inputHeightClass =
        height === "small" ? "phone-field__input--height-small" : height === "flexible" ? "phone-field__input--height-flexible" : "";
    const wrapperHeightClass =
        height === "small" ? "phone-field__input-wrapper--height-small" : height === "flexible" ? "phone-field__input-wrapper--height-flexible" : "";
    const buttonHeightClass =
        height === "small"
            ? "phone-field__country-button--height-small"
            : height === "flexible"
              ? "phone-field__country-button--height-flexible"
              : "";

    const ref = useScrollIntoView<HTMLInputElement>(model);
    const containerRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: () => {
            if (snapshot.dropdownOpen) {
                model.closeDropdown();
                if (hasInteracted) {
                    model.onBlur();
                }
            }
        },
        enableSmartTouch: true,
        ignoredSelectors: [".checkbox-field", ".radio-field"],
    });

    useEffect(() => {
        if (!prefixRef.current) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setPrefixWidth(entry.contentRect.width + 10);
            }
        });

        observer.observe(prefixRef.current);
        return () => observer.disconnect();
    }, [snapshot.activeCountry]);

    const handleChangeCountry = useCallback(
        (code: string) => {
            model.changeCountry(code);
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        },
        [model],
    );

    const handleInteraction = useCallback(() => {
        if (!hasInteracted) {
            setHasInteracted(true);
        }
    }, [hasInteracted]);

    // Restore cursor position after render (from model)
    useLayoutEffect(() => {
        if (!inputRef.current) return;
        if (!snapshot.cursorPosition) return;

        const {start, end} = snapshot.cursorPosition;
        inputRef.current.setSelectionRange(start, end);
        model.clearCursorPosition();
    }, [snapshot.formattedValue, snapshot.cursorPosition, model]);

    return (
        <div ref={containerRef} className={`phone-field ${className || ""}`}>
            {label && (
                <label className={`phone-field__label ${labelClass}`}>
                    {label}
                    {snapshot.isRequired && <span className="phone-field__required">*</span>}
                </label>
            )}

            <div className="phone-field__container" ref={ref}>
                {/* Country Selector with Floating Dropdown */}
                <FloatingDropdown
                    isOpen={snapshot.dropdownOpen}
                    onOpenChange={(open) => {
                        if (!open) {
                            model.closeDropdown();
                        }
                    }}
                    placement="bottom-start"
                    offsetValue={4}
                    trigger={
                        <button
                            type="button"
                            onClick={() => {
                                handleInteraction();
                                model.toggleDropdown();
                            }}
                            className={`phone-field__country-button ${buttonHeightClass} ${!showFlag ? "phone-field__country-button--no-flag" : ""}`}
                        >
                            {showFlag && (
                                <span className="f32 phone-field__flag" style={{marginRight: "8px"}}>
                                    <span className={`flag ${snapshot.activeCountry.toLowerCase()}`} />
                                </span>
                            )}
                            <span className="phone-field__country-code">{snapshot.activeCountry}</span>
                            <svg
                                className={`phone-field__dropdown-arrow ${snapshot.dropdownOpen ? "open" : ""}`}
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="currentColor"
                            >
                                <path d="M6 8L2 4h8L6 8z" />
                            </svg>
                        </button>
                    }
                    className="phone-field__country-selector"
                    dropdownClassName="phone-field__floating-dropdown"
                >
                    <CountryDropdown
                        isOpen={snapshot.dropdownOpen}
                        search={snapshot.search}
                        filteredPhones={snapshot.filteredPhones}
                        activeCountry={snapshot.activeCountry}
                        onSearchChange={(value) => model.searchFilter(value)}
                        onCountrySelect={handleChangeCountry}
                    />
                </FloatingDropdown>

                {/* Phone Input */}
                <div className={`phone-field__input-wrapper ${wrapperHeightClass} ${snapshot.shouldShowError ? "has-error" : ""}`}>
                    <div ref={prefixRef} className={`phone-field__prefix ${prefixFontSizeClass}`}>
                        +{snapshot.phoneCode}
                    </div>
                    <input
                        ref={inputRef}
                        type="tel"
                        autoComplete={autoComplete}
                        value={snapshot.formattedValue || snapshot.value}
                        onChange={(e) => {
                            // Save cursor position before processing
                            model.setCursorPosition(e.target.selectionStart ?? 0, e.target.selectionEnd ?? 0);
                            model.onChangePhone(e.target.value);
                        }}
                        onFocus={handleInteraction}
                        onBlur={() => {
                            if (!snapshot.dropdownOpen && hasInteracted) {
                                model.onBlur();
                            }
                        }}
                        onPaste={(e) => {
                            handlePasteAtCursor(e, (value) => model.onChangePhone(value));
                        }}
                        placeholder={snapshot.inputPlaceholder}
                        disabled={snapshot.isLoading}
                        className={`phone-field__input ${fontSizeClass} ${inputHeightClass} ${snapshot.shouldShowError ? "has-error" : ""}`}
                        style={{paddingLeft: `${prefixWidth + 10}px`}}
                    />
                </div>
            </div>

            {/* Error Message */}
            {snapshot.shouldShowError && snapshot.error && (
                <span className="phone-field__error">
                    {tValidation(snapshot.error.message as Parameters<typeof tValidation>[0], snapshot.error.messageParams)}
                </span>
            )}

            {/* Loading State */}
            {snapshot.isLoading && <div className="phone-field__loading">{t("loadingCountries")}</div>}
        </div>
    );
}
